import * as React from 'react';
import { CloseEmptyIcon, SearchIcon } from '@align-cpf/uikit-icons';
import { IInputTextProps, InputText } from '../../atoms/InputText';
import { Icon } from '../../atoms';
import { createLocators } from '@u1/locators';
import clsx from 'clsx';
import useStyles from './InputSearch.styles';

export interface IInputSearchProps
  extends Omit<IInputTextProps, 'onChange' | 'value'> {
  /**
   * Minimum search length value that enables
   */
  minSearchLength?: number;
  /**
   * Callback than invokes on Enter or Search icon press
   */
  onSearch: (value: string) => unknown;
  /**
   * Callback than invokes input change
   */
  onChange: (value: string) => unknown;
  /**
   * Max allowed search string lenght
   */
  maxLenght?: number;
  /**
   * Current search string
   */
  value: string;
}

export const inputSearchLocators = createLocators({
  name: 'InputSearch',
  componentLocators: [
    'container',
    'input',
    'iconSearch',
    'iconClear',
    'iconSearchContainer',
    'iconClearContainer',
  ],
});
const { locators } = inputSearchLocators();

const InputSearch = React.forwardRef<HTMLInputElement, IInputSearchProps>(
  (
    {
      onSearch,
      value,
      maxLength = 255,
      minSearchLength = 3,
      onChange,
      placeholder = 'Search',
      disabled,
      ...props
    },
    ref,
  ) => {
    const classes = useStyles();
    InputSearch.displayName = 'InputSearch';
    const canSearch = value && value.length >= minSearchLength;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      if (value.length > maxLength || value === undefined) {
        return;
      }
      onChange(value.trimStart());
    };

    const clearValue = () => {
      onChange('');
    };

    const handleSearch = () => {
      if (canSearch) {
        onSearch(value);
      }
    };

    return (
      <div {...locators.container()} className={classes.root}>
        <InputText
          {...props}
          ref={ref}
          type={'text'}
          onChange={handleChange}
          _UNSAFE_classname={classes.search}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
        />
        <div
          {...locators.iconSearchContainer({ disabled })}
          className={clsx({
            [classes.iconDisabled]: disabled,
            [classes.searchIcon]: true,
            [classes.icon]: true,
          })}
          onClick={handleSearch}
        >
          <Icon icon={SearchIcon} locatorsOverrides={locators.iconSearch()} />
        </div>
        {Boolean(value) && !disabled && (
          <div
            {...locators.iconClearContainer()}
            className={clsx({
              [classes.clearIcon]: true,
              [classes.icon]: true,
            })}
            onClick={clearValue}
          >
            <Icon
              icon={CloseEmptyIcon}
              locatorsOverrides={locators.iconClear()}
            />
          </div>
        )}
      </div>
    );
  },
);

export default InputSearch;
